import React, { useState, useEffect } from "react";
import {
  Card,
  Text,
  Group,
  Modal,
  ScrollArea,
  ActionIcon,
  Divider,
  Loader,
  Center,
} from "@mantine/core";
import {
  IconChevronRight,
  IconMailCheck,
  IconMailboxOff,
  IconRefresh,
} from "@tabler/icons-react";
import "./EmailHistory.scss";
import { useParams } from "react-router-dom";
import type { Email } from "../../../types";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import EmailPreview from "./EmailPreview/EmailPreview";
import { getEmailData } from "@api/email";

interface EmailHistoryProps {
  refresh: number;
}

const EmailHistory: React.FC<EmailHistoryProps> = ({ refresh }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const isMobile = useMediaQuery("(max-width: 50em)");

  const clientId = useParams().userId;
  const fetchEmails = async (): Promise<void> => {
    try {
      const emailHistory = await getEmailData(clientId as string);
      if (emailHistory?.status === 200) {
        setEmails(emailHistory.data.content);
        localStorage.setItem(
          "emails",
          JSON.stringify(emailHistory.data.content)
        );
      }
    } catch (error) {
      console.error("Failed to fetch emails:", error);
      // Handle error appropriately - maybe show an error message to user
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchEmails();
    }

    return () => {
      localStorage.removeItem("emails");
    };
  }, [clientId, refresh]);

  const handleEmailClick = (email: Email): void => {
    setSelectedEmail(email);
    open();
  };

  const handleRefreshClick: () => Promise<void> = async () => {
    setLoading(true);
    await fetchEmails();
    setLoading(false);
  };

  const renderContent = (): JSX.Element | null => {
    if (loading) {
      return (
        <Center style={{ height: 300 }}>
          <div className="emailLoading">
            <Loader size="lg" />
            <Text fw={700}>Refreshing...</Text>
          </div>
        </Center>
      );
    }

    if (emails.length > 0) {
      return (
        <>
          <Group>
            <div className="emailHeader">
              <div className="emailHeaderTitle">
                <div className="emailHeaderTitle-left">
                  <IconMailCheck size={18} />
                  <Text fw={700} size="lg">
                    Emails History
                  </Text>
                </div>
                <div className="emailHeaderTitle-right">
                  <ActionIcon
                    variant="transparent"
                    onClick={handleRefreshClick}
                  >
                    <IconRefresh size={18} />
                  </ActionIcon>
                  <Text c="dimmed">{emails.length} Emails</Text>
                </div>
              </div>

              <Divider size="xs" />
            </div>
          </Group>

          <ScrollArea h={600}>
            <Group gap={0}>
              {emails.map((email, index) => {
                const date = new Date(email.sendDate);
                const formattedDate = date.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                });

                return (
                  <Card
                    p="lg"
                    className="emailCard"
                    key={`${email.id}-${index}`}
                    onClick={() => handleEmailClick(email)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="emailList">
                      <div className="emailInfo">
                        {/* 
                          FIX: Separate <Text> components so we don't nest <p> inside <p> 
                        */}
                        <Text className="emailSubject" size="md">
                          {email.subject}
                        </Text>
                        <Text className="emailDate" c="dimmed" size="sm">
                          {formattedDate}
                        </Text>
                        <ActionIcon variant="transparent">
                          <IconChevronRight />
                        </ActionIcon>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </Group>
          </ScrollArea>
        </>
      );
    }

    return (
      <div className="empty-mail">
        <IconMailboxOff size={50} className="empty-icon" />
        <Text size="lg" c="dimmed">
          No Emails Sent
        </Text>
      </div>
    );
  };

  return (
    <div className="email-history-container">
      <div>{renderContent()}</div>
      <Modal
        className="email-history-modal"
        opened={opened}
        onClose={close}
        fullScreen={isMobile}
        centered
        transitionProps={{ transition: "fade", duration: 200 }}
        size="80%"
        p="0"
        radius="md"
      >
        <EmailPreview
          selectedEmail={selectedEmail}
          emails={emails}
          onEmailSelect={setSelectedEmail}
        />
      </Modal>
    </div>
  );
};

export default EmailHistory;
